import {
	Grid,
	FormLabel,
	styled,
	Radio,
	RadioGroup,
	FormControlLabel,
	FormHelperText,
} from "@mui/material";
import { types } from "api";
import { InfoSnackBar } from "components/InfoSnackBar";
import { StyledButton } from "components/StyledButton";
import {
	capabilities,
	DisplayGeneralCapability,
} from "components/FivePointScaleSelector/GeneralCapabilities";
import { ACTIVITIES_FETCH_ERR } from "messages";
import { useEffect, useState } from "react";
import { allSelected } from "./EvaluationHelpers";
import { ReactComponent as BlankIcon } from "assets/icons/sentiment/blank.svg";
import { ReactComponent as NeutralIcon } from "assets/icons/sentiment/neutral.svg";
import { ReactComponent as GoodIcon } from "assets/icons/sentiment/good.svg";
import { ReactComponent as SatisfiedIcon } from "assets/icons/sentiment/satisfied.svg";
import { ReactComponent as VerySatisfiedIcon } from "assets/icons/sentiment/very_satisfied.svg";

// import {  } from "api/types";

const PREFIX = "AddStudentEvaluationPage";

const classes = {
	groups: `${PREFIX}-groups`,
	title: `${PREFIX}-title`,
	button: `${PREFIX}-button`,
	capabilityLabel: `${PREFIX}-capabilityLabel`,
	form: `${PREFIX}-form`,
	radio: `${PREFIX}-radio`,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Root = styled("form")(({ theme }) => ({
	[`&.${classes.form}`]: {
		flexGrow: 1,
		padding: 20,
	},

	[`& .${classes.button}`]: {
		marginRight: 5,
		float: "right",
	},

	[`& .${classes.title}`]: {
		marginTop: 5,
		marginBottom: 15,
		float: "left",
	},

	[`& .${classes.radio}`]: {
		marginLeft: theme.spacing(4),
	},
}));

export interface CapabilityEvaluationProps {
	userIdParam?: string;
	capabilityGrade: types.GradeById[];
	setCapabilityGrade: (capabilityGrade: types.GradeById[]) => void;
	hasCapability: boolean;
	onSubmit: (event: React.FormEvent) => void;
}

export const CapabilityEvaluation = ({
	capabilityGrade,
	setCapabilityGrade,
	hasCapability,
	onSubmit,
}: CapabilityEvaluationProps): JSX.Element => {
	const [err, setErr] = useState(false);
	const [loaded, setLoaded] = useState(false);
	const [graded, setGraded] = useState(0);

	useEffect(() => {
		if (hasCapability && !loaded) {
			capabilityGrade.forEach(function (eg) {
				const capability = capabilities.find((c) => {
					return eg.id === c.id;
				});
				if (capability) {
					capability.grade = +eg.grade;
				}
			});
			setGraded(capabilityGrade.length);
			setLoaded(true);
		} else if (!hasCapability) {
			capabilities.forEach(function (capability) {
				capability.grade = 0;
			});
		}
	}, [hasCapability, loaded, capabilityGrade]);

	const handleChange =
		(field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
			const eg = capabilityGrade.find((g) => {
				return g.id === field;
			});
			if (eg) {
				eg.grade = event.target.value;
			} else {
				const newGrade: types.GradeById = {
					id: field,
					grade: event.target.value,
				};
				capabilityGrade.push(newGrade);
			}
			setCapabilityGrade(capabilityGrade);
			setGraded(capabilityGrade.length);
		};

	return (
		<Root className={classes.form} onSubmit={onSubmit}>
			{!allSelected(graded, capabilities.length) && (
				<FormHelperText>Please add all the evaluations</FormHelperText>
			)}

			<Grid container spacing={1}>
				<Grid item xs={2} />
				<Grid
					item
					xs={10}
					container
					direction="row"
					justifyContent="space-between"
					alignItems="flex-start"
				>
					<Grid item xs={2}>
						<BlankIcon height="100" width="100" />
					</Grid>
					<Grid item xs={2}>
						<NeutralIcon height="100" width="100" />
					</Grid>
					<Grid item xs={2}>
						<SatisfiedIcon height="100" width="100" />
					</Grid>
					<Grid item xs={2}>
						<GoodIcon height="100" width="100" />
					</Grid>
					<Grid item xs={2}>
						<VerySatisfiedIcon height="100" width="100" />
					</Grid>
					<Grid item xs={2}>
						<StyledButton
							variant="contained"
							color="primary"
							className={classes.button}
							type="submit"
							data-testid="submit-edit-capability"
							disabled={!allSelected(graded, capabilities.length)}
						>
							Submit Edit
						</StyledButton>
					</Grid>
				</Grid>

				{capabilities.map((capability: DisplayGeneralCapability) => (
					<Grid item xs={12} key={`${capability.name}-row`}>
						<Grid
							container
							direction="row"
							justifyContent="space-between"
							alignItems="center"
						>
							<Grid item xs={2} key={`${capability.name}-label`}>
								<FormLabel component="legend">{capability.name}</FormLabel>
							</Grid>
							<Grid
								item
								xs={10}
								key={`${capability.name}-raidoGroup`}
								justifyContent="space-between"
							>
								<RadioGroup
									name={`${capability.name}-${capability.name}-group`}
									aria-label={`${capability.name}-${capability.name}-group`}
									row
									defaultValue={capability.grade ? capability.grade : 0}
									onChange={handleChange(capability.id)}
								>
									{[1, 2, 3, 4, 5].map((value) => (
										<Grid item xs={2} key={`${capability.name}-${value}-raido`}>
											<FormControlLabel
												key={value}
												value={value.toString()}
												className={classes.radio}
												control={<Radio />}
												label=""
											/>
										</Grid>
									))}
								</RadioGroup>
							</Grid>
						</Grid>
					</Grid>
				))}
			</Grid>

			<InfoSnackBar
				isOpen={err}
				onClose={() => setErr(false)}
				message={ACTIVITIES_FETCH_ERR}
				severity="error"
			/>
		</Root>
	);
};
